import { isSignal, WritableSignal } from '@angular/core';
import Collection from '@arcgis/core/core/Collection';
import {
  ButtonParams,
  getThemeRuntimeCurrentTheme,
  isThemeAwareLayer,
  JepLayer,
  LayerConfiguration,
  LegendParams,
  Plugin,
  PluginConfigurationRecord,
  PluginID,
  PluginMap
} from '@jeppesen-foreflight/jmkw-types';
import type { ThemeColor } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { Subject } from 'rxjs';

export function pluginArrayToMap(plugins: Plugin[] | PluginMap): PluginMap {
  if (plugins instanceof PluginMap) {
    return plugins;
  }

  const pluginMap = new PluginMap();

  for (const plugin of plugins) {
    if (pluginMap.has(plugin.id)) {
      throw new Error(
        'Each plugin must have a unique id. Found ' +
          plugin.id +
          ' more than once.'
      );
    } else {
      pluginMap.set(plugin.id, plugin);
    }
  }

  return pluginMap;
}

export function pluginToChildren(plugin: Plugin): [PluginID, Plugin] {
  return [plugin.id, plugin];
}

/**
 * Generic function to apply configuration properties from a configuration object to the target object,
 * while explicitly skipping properties whose keys are included in the `exclusions` array.
 *
 * @param target The target object on which to apply configurations.
 * @param config The configuration object containing key-value pairs.
 * @param exclusions An array of keys that should not be applied.
 * @param logger Optional logger instance for error reporting.
 */
export function applyConfigurations<T>(
  target: T,
  config: Partial<T>,
  exclusions: string[],
  logger?: NGXLogger
): void {
  try {
    Object.entries(config).forEach(([key, value]) => {
      if (!exclusions.includes(String(key))) {
        setValue(target, key as keyof T, value as T[keyof T], logger);
      }
    });
  } catch (error) {
    if (logger) {
      logger.error('[PluginUtils] Error applying configuration:', error);
    } else {
      console.error('[PluginUtils] Error applying configuration:', error);
    }
  }
}

/**
 * Applies button configurations by skipping keys 'id' and 'buttons'.
 *
 * @param button The target button object.
 * @param buttonConfig The configuration to apply.
 */
export function applyButtonConfigurations(
  button: ButtonParams,
  buttonConfig?: Partial<ButtonParams>
): void {
  if (buttonConfig) {
    applyConfigurations<ButtonParams>(button, buttonConfig, ['id']);
  }
}

/**
 * Applies legend configurations by skipping keys 'id' and 'legends'.
 *
 * @param legend The target legend object.
 * @param legendConfig The configuration to apply.
 */
export function applyLegendConfigurations(
  legend: LegendParams,
  legendConfig?: Partial<LegendParams>
): void {
  if (legendConfig) {
    applyConfigurations<LegendParams>(legend, legendConfig, ['id']);
  }
}

/**
 * Applies layer configurations by skipping keys 'id' and 'layers', and
 * recursively applies configurations to sublayers if present.
 *
 * @param layer The target layer.
 * @param layerConfiguration The configuration object to apply.
 */
export function applyLayerConfigurations(
  layer: JepLayer,
  layerConfiguration?: LayerConfiguration,
  configs?: PluginConfigurationRecord
): void {
  if (!layerConfiguration) return;

  // Apply top-level properties, skipping 'id' and 'layers'
  applyConfigurations<JepLayer>(layer, layerConfiguration, ['id', 'layers']);

  // Recursively apply sublayer configs
  if (
    'layers' in layer &&
    layer.layers instanceof Collection &&
    typeof layer.layers.forEach === 'function'
  ) {
    layer.layers.forEach((sublayer) => {
      if (!sublayer || !('id' in sublayer)) return;
      const sublayerId = sublayer.id;

      let sublayerConfig: LayerConfiguration | undefined;

      // Case 1: Keyed object under layerConfiguration['layers']
      if (
        layerConfiguration['layers'] &&
        typeof layerConfiguration['layers'] === 'object' &&
        !(layerConfiguration['layers'] instanceof Collection)
      ) {
        sublayerConfig = (
          layerConfiguration['layers'] as Record<string, LayerConfiguration>
        )[sublayerId];
      }

      // Case 2: Collection under layerConfiguration['layers']
      if (
        !sublayerConfig?.['layers'] &&
        layerConfiguration?.['layers'] instanceof Collection
      ) {
        sublayerConfig = layerConfiguration['layers'].find(
          (entry: any) => entry.id === sublayerId
        );
      }

      // Case 3: Flattened sibling key (legacy support)
      if (!sublayerConfig && (layerConfiguration as any)[sublayerId]) {
        sublayerConfig = (layerConfiguration as any)[sublayerId];
      }

      if (sublayerConfig) {
        // **KEY CHANGE**: Use applyConfiguration for PseudoLayers to get buffering behavior
        if (typeof sublayer.applyConfiguration === 'function') {
          sublayer.applyConfiguration(sublayerConfig);
        } else {
          // Fallback to recursive call for regular layers
          applyLayerConfigurations(sublayer, sublayerConfig);
        }
      }
    });
  }

  reapplyRuntimeTheme(layer);
}

function reapplyRuntimeTheme(layer: unknown): void {
  const theme = getThemeRuntimeCurrentTheme();
  if (!theme) return;

  reapplyThemeToLayerTree(layer, theme);
}

function reapplyThemeToLayerTree(layer: unknown, theme: ThemeColor): void {
  if (isThemeAwareLayer(layer)) {
    const previousTheme = layer.currentTheme;
    layer.applyTheme(theme);

    if (previousTheme === theme) {
      forceThemeAwareLayerRestyle(layer, theme);
    }
  }

  const childLayers = (layer as any)?.layers ?? (layer as any)?.subLayers?.();
  if (childLayers?.forEach) {
    childLayers.forEach((childLayer: unknown) =>
      reapplyThemeToLayerTree(childLayer, theme)
    );
  }
}

function forceThemeAwareLayerRestyle(layer: unknown, theme: ThemeColor): void {
  const themeAwareLayer = layer as any;

  if (typeof themeAwareLayer.onThemeChange === 'function') {
    themeAwareLayer.onThemeChange(theme, theme);
    return;
  }

  if (typeof themeAwareLayer.updateLayerStyles === 'function') {
    themeAwareLayer.updateLayerStyles(theme);
  }
}

/**
 * Sets a value on the target object for a given key. It checks if the property is a
 * Subject or a WritableSignal and applies the update accordingly. If neither, it performs
 * a standard property assignment.
 *
 * @param obj The target object.
 * @param key The key for the property to set.
 * @param value The new value to assign.
 * @param logger Optional logger instance for error/warning reporting.
 */
export function setValue<T, K extends keyof T>(
  obj: T,
  key: K,
  value: T[K],
  logger?: NGXLogger
): void {
  try {
    if (obj[key] instanceof Subject) {
      // If the property is a Subject, emit the new value.
      (obj[key] as Subject<T[K]>).next(value);
    } else if (isSignal(obj[key])) {
      // If the property is a WritableSignal, set the new value.
      const signal = obj[key] as unknown as WritableSignal<T[K]>;
      if (typeof signal.set === 'function') {
        signal.set(value);
      } else {
        if (logger) {
          logger.warn(`[PluginUtils] Invalid signal for key: ${String(key)}`);
        } else {
          console.warn(`[PluginUtils] Invalid signal for key: ${String(key)}`);
        }
      }
    } else {
      // Direct assignment for standard properties.
      obj[key] = value;
    }
  } catch (error) {
    if (logger) {
      logger.error(`[PluginUtils] Error setting value for key ${String(key)}:`, error);
    } else {
      console.error(`[PluginUtils] Error setting value for key ${String(key)}:`, error);
    }
  }
}
